import React, { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import arePropsEqual from 'react-fast-compare';
import { useSelector } from 'react-redux';
import { Icon } from '@modulor/react';
import { IconButtonWithAnalytics } from '../IconButton/IconButton';
import { useIsFavouriteGamesEnabled } from '../../../hooks/useIsFavouriteGamesEnabled';
import { useIsSSCOMBrand } from '../../../hooks/useIsSSCOMBrand';
import { useIsFavoriteGame, useToggleFavouriteGame } from './hooks';
import {
  brandSelector,
  isLoggedInSelector,
} from '../../../redux/reducers/app/params';
import { getDataTestAttrs } from '../../../utils/testing-locators';
import {
  FAVOURITE_ACTIVE,
  FAVOURITE_NOT_ACTIVE,
} from '../../../constants/dataTestIds';
import { ANALYTICS_EVENT_DETAIL } from '../../../constants/analytics';
import { SCCOM_BRAND } from '../../../constants/noNameBrands';
import styles from './FavoriteButton.module.css';

const getIconProps = (brand, isFavorite) => {
  switch (brand) {
    case SCCOM_BRAND:
      return {
        name: isFavorite ? 'heart_filled' : 'heart_outlined',
        fill: 'icon-always-white',
        size: 24,
      };

    default:
      return {
        name: isFavorite ? 'star_filled' : 'star_outlined',
        fill: 'var(--icon-main)',
        size: 20,
      };
  }
};

const FavoriteButton = ({
  game,
  className,
  analyticsData,
  iconSize,
  productType,
  ...props
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const isEnabledFavoriteGames = useIsFavouriteGamesEnabled();
  const brand = useSelector(brandSelector);
  const isSCCOM = useIsSSCOMBrand();

  const isLoggedIn = useSelector(isLoggedInSelector);
  const isFavorite = useIsFavoriteGame(game.id, productType);
  const toggle = useToggleFavouriteGame(game.id, productType, {
    onSuccess: () => setIsFetching(false),
  });
  const handleFavoriteClick = useCallback(() => {
    setIsFetching(true);
    toggle();
  }, [toggle, setIsFetching]);

  return isLoggedIn && isEnabledFavoriteGames ? (
    <IconButtonWithAnalytics
      onClick={!isFetching ? handleFavoriteClick : undefined}
      className={classNames(
        styles.button,
        isSCCOM && styles.button_sc,
        className,
        isFetching && styles.loading
      )}
      analyticsData={{
        ...(analyticsData || {}),
        ...(isFavorite
          ? { event_detail: ANALYTICS_EVENT_DETAIL.REMOVE_FROM_FAVORITE }
          : {}),
      }}
      {...props}
    >
      <Icon
        {...getIconProps(brand, isFavorite)}
        size={iconSize}
        {...getDataTestAttrs(
          isFavorite ? FAVOURITE_ACTIVE : FAVOURITE_NOT_ACTIVE
        )}
      />
    </IconButtonWithAnalytics>
  ) : null;
};

FavoriteButton.propTypes = {
  game: PropTypes.shape({
    id: PropTypes.string,
    translationKey: PropTypes.string,
    provider: PropTypes.string,
    themes: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.string),
  }),
  analyticsData: PropTypes.shape({
    screen_name: PropTypes.string,
    screen_area: PropTypes.string,
    screen_area_detail: PropTypes.string,
    event_detail: PropTypes.string,
    name: PropTypes.string,
    schema: PropTypes.string,
  }),
  productType: PropTypes.string,
};

export default memo(FavoriteButton, arePropsEqual);
